import { useMemo } from 'react';
import type { EnumColumnDeclaration } from '../../services/metaInfoApi';
import { findWithSchemaFold } from '../../utils/metaInfoSchemaMatch';

export function resolveJsonEnumDecl(
  decls: EnumColumnDeclaration[],
  schema: string,
  table: string,
  column: string,
  path: string,
): EnumColumnDeclaration | undefined {
  return findWithSchemaFold(
    decls,
    schema,
    (d) => d.table === table && d.column === column && d.path === path,
  );
}

export type JsonEnumPickerSlot =
  | { available: true; declared: true; pathKind: 'scalar' | 'array' }
  | { available: true; declared: false } // path eligible but undeclared → declare flow
  | { available: false };

export function useJsonEnumPickerSlot(args: {
  metaInfo: { enumColumns: EnumColumnDeclaration[] } | null | undefined;
  schema?: string;
  table?: string;
  column?: string;
  path?: string;
}): JsonEnumPickerSlot {
  const { metaInfo, schema, table, column, path } = args;
  return useMemo<JsonEnumPickerSlot>(() => {
    // Kusto/ADX is schemaless (`schema: ''`, a present-and-valid schema — not
    // missing). JSON-path enum sampling + filtering is now supported for Kusto
    // (`kustoEnumExtractor.sample` handles scalar/array/wildcard paths; the
    // filter side compiles array/wildcard enums via an mv-apply projection), so
    // use `== null` here like the other schemaless guards — do NOT reintroduce
    // `!schema`, which would hide the picker for every Kusto column.
    if (schema == null || !table || !column || !path) return { available: false };
    const decl = resolveJsonEnumDecl(
      metaInfo?.enumColumns ?? [],
      schema,
      table,
      column,
      path,
    );
    if (decl) return { available: true, declared: true, pathKind: decl.pathKind ?? 'scalar' };
    return { available: true, declared: false };
  }, [metaInfo, schema, table, column, path]);
}
